import React, { useEffect, useState } from 'react';
import { Checkbox, ModalFeedback, SparkIcon, SparkModalPortal } from '@spark';

/**
 * Gate modal shown when "Let's Train" is clicked while Generate-with-AI /
 * Retrieval Agent flows or dataset/benchmark uploads are still running, or —
 * with `finalizing` — while the flows finished but their data is still being
 * processed into the datasets (the auto-train settle window). The
 * checked-by-default checkbox schedules auto-train ("Start training as soon
 * as data is ready"); when the user unchecks it, a warning explains that
 * training starts now on the current, partial data.
 */
const DataFlowsRunningModal = ({ visible, flowCount = 1, finalizing = false, onCancel, onStart }) => {
    const [waitForData, setWaitForData] = useState(true);

    // Re-check the box on every open — waiting for the data is the
    // recommended default.
    useEffect(() => {
        if (visible) setWaitForData(true);
    }, [visible]);

    const title = finalizing
        ? 'Data is still being processed'
        : flowCount === 1
            ? 'A data flow is still running'
            : 'Data flows are still running';
    const message = finalizing
        ? 'All data flows finished, but their data is still being processed — it isn’t fully in the training set yet.'
        : flowCount === 1
            ? 'A data flow is still running. Its data isn’t included in the training set yet.'
            : `${flowCount} data flows are still running — their data is not part of the training set yet.`;

    return (
        <SparkModalPortal open={visible} onClose={onCancel} size="text">
            <ModalFeedback
                feedbackType="info"
                title={title}
                content={(
                    <div className="data-flows-modal__body fs-unmask">
                        <p className="data-flows-modal__message">{message}</p>
                        <Checkbox
                            label="Start training as soon as data is ready"
                            status={waitForData ? 'active' : 'inactive'}
                            onChange={() => setWaitForData((previous) => !previous)}
                        />
                        {!waitForData ? (
                            <div className="data-flows-modal__warning" role="alert">
                                <span className="data-flows-modal__warning-icon" aria-hidden="true">
                                    <SparkIcon slug="circle-alert-triangle" size="small" />
                                </span>
                                <span>
                                    Training starts now on the current data only. Missing data
                                    may affect the quality of the resulting model.
                                </span>
                            </div>
                        ) : null}
                    </div>
                )}
                showClose
                showOverlay={false}
                primaryLabel="Start"
                primaryDisableWhenReadOnly
                tertiaryLabel="Cancel"
                onCloseClick={onCancel}
                onPrimaryClick={() => onStart?.(waitForData)}
                onTertiaryClick={onCancel}
            />
        </SparkModalPortal>
    );
};

export default DataFlowsRunningModal;
